import { Divider, IconButton, Stack } from '@mui/material';
import { isTieredSubscriptionProductVariant } from '@vy/services';
import type { TieredSubscriptionProductVariant } from '@vy/types';
import type { PriceTier, UsageProduct } from '@vy/types';
import { isNotNullOrUndefined, raise, removeInIndex } from '@vy/utils';
import React, { useCallback, useState } from 'react';
import { get, useFormContext } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import { VayuTextField } from '../../../../../../../components/design/vayu-theme-components/VayuTextField';
import { CloseIcon } from '../../../../../../../components/icons';
import { useFormErrorNotificationContext } from '../../../../../../../contexts/form-error-notification-context/form-error-notification-context';
import { getDefaultTieredSubscriptionUsageProductVariant } from '../../../../create-plan-page-consts/add-item-button-consts';
import { useChargeContext } from '../../../charge-context';
import type { PlanInputForm } from '../../../CreatePlanForm/create-plan-form-types/plan-input-form';
import type { CreatePlanTab } from '../../../CreatePlanPageTabs/create-plan-page-types/create-plan-tab';
import { PlanChargesContainer } from '../../../PlanChargesContainer';
import ProductDotMenuDropdown from '../../../ProductDotMenu';
import { UsageProductGroupDialogForm } from '../UsageProductGroupDialogForm';
import { UsageProductVariantHeader } from '../UsageProductVariantHeader';
import { UsageProductVariantSummary } from '../UsageProductVariantSummary';
import { UsageProductVariantTable } from '../UsageProductVariantTable';
import { usePlanCreationFormUtils } from '../../../../../../../contexts/create-plan-context/CreatePlanProvider/hooks/use-plan-creation-form-utils';

export const TieredSubscriptionUsageChargeEdit: React.FC = () => {
  const { item: product, itemIndex: productIndex, currency } = useChargeContext<UsageProduct>();
  const { registerErrorField } = useFormErrorNotificationContext<PlanInputForm, CreatePlanTab>();
  const {
    register,
    getValues,
    formState: { errors },
    setValue,
  } = useFormContext<PlanInputForm>();
  const [isProductGroupDialogOpen, setIsProductGroupDialogOpen] = useState(false);
  const { isFieldEditable } = usePlanCreationFormUtils();

  const productNameRegister = register(`usageProducts.${productIndex}.name`, {
    value: product.name,
  });

  registerErrorField(productNameRegister.name);

  const addVariant = () => {
    const newVariant = getDefaultTieredSubscriptionUsageProductVariant();

    const updatedProductVariants = [...product.variants, newVariant];
    setValue(`usageProducts.${productIndex}.variants`, updatedProductVariants);
  };

  const isGroupedProduct = product.variants.length > 1;

  const onDelete = useCallback(() => {
    const updatedProducts = removeInIndex(getValues('usageProducts'), productIndex);
    setValue('usageProducts', updatedProducts);
  }, [productIndex]);

  const handleConvertToGroup = () => {
    setIsProductGroupDialogOpen(true);
  };

  function handleGroupDelete() {
    const [firstVariant] = product.variants;
    setValue(`usageProducts.${productIndex}.name`, undefined);
    setValue(`usageProducts.${productIndex}.variants`, [firstVariant]);
  }

  return (
    <div
      className={twMerge(
        'flex flex-col gap-y-4',
        isFieldEditable(`usageProducts.${productIndex}`) ? '' : 'pointer-events-none opacity-40',
      )}
    >
      <PlanChargesContainer
        key={product.id}
        className="relative px-0 pb-0"
        id={`create-product-${productIndex}`}
      >
        {isGroupedProduct && (
          <div className="flex max-w-xs px-8 pt-8">
            <VayuTextField
              placeholder="Text here"
              {...productNameRegister}
              label="Group Name"
              defaultValue={getValues(productNameRegister.name) ?? ''}
              helperText={get(errors, productNameRegister.name)?.message}
              error={isNotNullOrUndefined(get(errors, productNameRegister.name))}
              focused
            />
          </div>
        )}

        <div>
          <div className="absolute right-3 top-3">
            <Stack
              direction="row"
              divider={<Divider orientation="vertical" flexItem variant="middle" />}
              sx={{
                columnGap: 1
              }}
            >
              <ProductDotMenuDropdown
                isGrouped={isGroupedProduct}
                onCreateGroup={handleConvertToGroup}
                onDeleteGroup={handleGroupDelete}
              />

              <IconButton onClick={onDelete}>
                <CloseIcon
                  color="#0D1B27DE"
                  style={{
                    transform: 'scale(0.7)',
                  }}
                />
              </IconButton>
            </Stack>
          </div>

          <UsageProductGroupDialogForm
            open={isProductGroupDialogOpen}
            onClose={() => setIsProductGroupDialogOpen(false)}
            onCreateClick={() => !isGroupedProduct && addVariant()}
            productIndex={productIndex}
          />

          <div>
            {product.variants.map((variant, variantIndex) => {
              if (!isTieredSubscriptionProductVariant(variant)) {
                raise('Invalid product variant type');
              }

              return (
                <div key={variant.id} className="even:bg-product-bk pt-10">
                  <UsageProductVariantHeader
                    productVariant={variant}
                    productVariantIndex={variantIndex}
                    productIndex={productIndex}
                    className="px-8"
                  />
                  <UsageProductVariantTable
                    key={variant.id}
                    tiers={getTiers(variant)}
                    productIndex={productIndex}
                    productVariantIndex={variantIndex}
                    isFlatRate
                  />
                </div>
              );
            })}
          </div>
          <UsageProductVariantSummary
            editable
            product={product}
            productIndex={productIndex}
            isGroupedProduct={isGroupedProduct}
            currency={currency}
          />
        </div>
      </PlanChargesContainer>
    </div>
  );
};

function getTiers(productVariant: TieredSubscriptionProductVariant): Partial<PriceTier>[] {
  const tiers = productVariant.pricing?.priceConfiguration?.tiers as Partial<PriceTier>[];

  return tiers ?? ([] as Partial<PriceTier>[]);
}
